import { useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { OfferTemplatePicker } from "@/components/OfferTemplatePicker";
import type { OfferTemplateId } from "@/types/offerTemplates";

function LayoutColumnsIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="currentColor"
      aria-hidden
    >
      <rect x="3" y="4" width="5" height="16" rx="0.5" />
      <rect x="10" y="4" width="5" height="16" rx="0.5" />
      <rect x="17" y="4" width="4" height="16" rx="0.5" />
    </svg>
  );
}

export type OfferPreviewWithTemplateSidebarProps = {
  templateId: OfferTemplateId;
  onTemplateChange: (id: OfferTemplateId) => void;
  children: ReactNode;
};

/** Podgląd oferty z wysuwanym panelem wyboru szablonu (live update templateId). */
export function OfferPreviewWithTemplateSidebar({
  templateId,
  onTemplateChange,
  children,
}: OfferPreviewWithTemplateSidebarProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex w-full min-w-0 items-stretch">
      <div className="relative min-w-0 flex-1">
        <button
          type="button"
          className={cn(
            "no-print absolute right-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-background/95 text-foreground shadow-md transition-opacity duration-200 hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            sidebarOpen && "pointer-events-none opacity-0"
          )}
          style={{ width: 36, height: 36 }}
          aria-label="Wybierz szablon oferty"
          aria-expanded={sidebarOpen}
          onClick={() => setSidebarOpen(true)}
        >
          <LayoutColumnsIcon />
        </button>
        {children}
      </div>
      <aside
        className="no-print shrink-0 overflow-hidden border-l border-border/60 bg-background"
        style={{
          width: sidebarOpen ? 180 : 0,
          transition: "width 0.22s ease",
        }}
        aria-hidden={!sidebarOpen}
      >
        <div className="flex h-full w-[180px] flex-col">
          <div className="flex items-center justify-between border-b border-border/50 px-2.5 py-2">
            <span className="text-xs font-semibold text-foreground">Szablon</span>
            <button
              type="button"
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Zamknij wybór szablonu"
              onClick={() => setSidebarOpen(false)}
            >
              ✕
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
            <OfferTemplatePicker
              variant="sidebar"
              value={templateId}
              onChange={onTemplateChange}
            />
          </div>
        </div>
      </aside>
    </div>
  );
}
